import React, { useState } from 'react';
import { downloadElementAsPdf } from '../../utils/exportPdf';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatDate } from '../../utils/helpers';

const TITLES = {
  invoice: 'Tax Invoice',
  challan: 'Delivery Challan',
  purchaseBill: 'Purchase Bill',
  salesReturn: 'Credit Note',
  purchaseReturn: 'Debit Note',
  salesOrder: 'Sales Order',
  purchaseOrder: 'Purchase Order',
  receipt: 'Receipt Voucher',
  payment: 'Payment Voucher',
  expense: 'Expense Voucher',
  contra: 'Contra Voucher',
  journal: 'Journal Voucher',
  production: 'Production Voucher',
  stockIssue: 'Stock Issue Voucher',
};

const VOUCHER_TYPES = ['receipt', 'payment', 'expense', 'contra', 'journal'];
const PURCHASE_DOCS = ['purchaseBill', 'purchaseReturn', 'purchaseOrder'];

export default function PrintPreview({ type, data, onClose }) {
  const { state } = useApp();
  const co = state.company;
  const party = state.parties.find(p => p.id === data.partyId);

  const isVoucher = VOUCHER_TYPES.includes(type);
  const isProduction = type === 'production';
  const isStockIssue = type === 'stockIssue';
  const isPurchaseDoc = PURCHASE_DOCS.includes(type);
  const itemNameById = (id) => state.items.find(i => i.id === id)?.name || '-';
  const itemUnitById = (id) => state.items.find(i => i.id === id)?.unit || '';
  const docNo = data.invoiceNo || data.challanNo || data.entryNo || data.returnNo || data.orderNo
    || data.receiptNo || data.paymentNo || data.expenseNo || data.contraNo || data.productionNo || data.issueNo;

  const handlePrint = () => window.print();

  const [generating, setGenerating] = useState(false);

  // Generate a clean one-page A4 PDF directly (shared util handles mobile too).
  const handleDownloadPdf = async () => {
    const el = document.getElementById('print-area');
    if (!el || generating) return;
    setGenerating(true);
    try {
      await downloadElementAsPdf(el, `${TITLES[type] || 'Document'}-${docNo || ''}`);
    } catch (e) {
      console.error('PDF generation failed:', e);
      if (typeof window !== 'undefined') window.alert('Could not generate the PDF. Please use the Print button instead.');
    } finally {
      setGenerating(false);
    }
  };

  const fullAddress = [co.address, [co.city, co.state, co.pincode].filter(Boolean).join(', '), co.country].filter(Boolean).join('\n');

  const voucherAmount = type === 'journal'
    ? (data.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0)
    : Number(data.amount) || 0;

  const partyHeading = type === 'payment' ? 'Paid To' : type === 'receipt' ? 'Received From' : 'Party';

  return (
    <div className="hide-scrollbar fixed inset-0 bg-white z-50 overflow-auto">
      {/* Per-page border, drawn on every printed page (print only) */}
      <div className="print-page-frame" aria-hidden="true" />
      <div className="no-print flex items-center gap-4 p-4 bg-gray-100 border-b sticky top-0">
        <button onClick={handleDownloadPdf} disabled={generating} className="btn btn-primary disabled:opacity-60">
          {generating ? 'Generating…' : 'Download PDF'}
        </button>
        <button onClick={handlePrint} className="btn btn-secondary">Print</button>
        <button onClick={onClose} className="btn btn-secondary">Close</button>
        <span className="text-xs text-gray-500 ml-auto">A4 · {TITLES[type] || 'Document'}</span>
      </div>

      <div className="max-w-[210mm] mx-auto p-8 bg-white" id="print-area">
        {/* Company header */}
        <div className="border-b-2 border-gray-800 pb-4 mb-6">
          {co.logo && (
            <div className="flex justify-center mb-2">
              <img src={co.logo} alt="Company logo" className="h-16 object-contain" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-center">{co.name}</h1>
          {co.legalName && co.legalName !== co.name && (
            <p className="text-center text-xs text-gray-500">({co.legalName})</p>
          )}
          <p className="text-center text-sm text-gray-600 whitespace-pre-line">{fullAddress}</p>
          <div className="flex justify-between text-xs text-gray-500 mt-2 flex-wrap gap-x-4">
            <span>GSTIN: {co.gstin}</span>
            {co.pan && <span>PAN: {co.pan}</span>}
            <span>Phone: {co.phone}{co.mobile ? ` / ${co.mobile}` : ''}</span>
            <span>Email: {co.email}</span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-center mb-6 uppercase">{TITLES[type] || 'Document'}</h2>

        {/* ─────────── Voucher layout ─────────── */}
        {isVoucher && (
          <div className="text-sm">
            <div className="grid grid-cols-2 gap-8 mb-6">
              <div>
                {party && (
                  <>
                    <h3 className="font-semibold mb-1">{partyHeading}:</h3>
                    <p className="font-medium">{party.name}</p>
                    {party.address && <p className="text-gray-600">{party.address}</p>}
                    {party.gstin && <p className="text-gray-600">GSTIN: {party.gstin}</p>}
                  </>
                )}
                {type === 'expense' && <p><span className="font-semibold">Expense Head:</span> {data.expenseHead}</p>}
                {type === 'contra' && (
                  <>
                    <p><span className="font-semibold">From Account:</span> {data.fromAccount}</p>
                    <p><span className="font-semibold">To Account:</span> {data.toAccount}</p>
                  </>
                )}
              </div>
              <div className="text-right">
                <p><span className="font-semibold">Voucher No:</span> {docNo}</p>
                <p><span className="font-semibold">Date:</span> {formatDate(data.date)}</p>
                {data.mode && <p><span className="font-semibold">Mode:</span> {data.mode}</p>}
                {data.paidFrom && <p><span className="font-semibold">Paid From:</span> {data.bankAccount || data.paidFrom}</p>}
                {data.bankAccount && data.mode && <p><span className="font-semibold">Bank A/c:</span> {data.bankAccount}</p>}
              </div>
            </div>

            {type === 'journal' ? (
              <table className="w-full border-collapse border border-gray-400 mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-400 px-2 py-1.5 text-left">Account</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-right">Debit</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lines || []).map((l, i) => (
                    <tr key={i}>
                      <td className="border border-gray-400 px-2 py-1">{l.account}</td>
                      <td className="border border-gray-400 px-2 py-1 text-right">{l.debit ? formatCurrency(l.debit) : '-'}</td>
                      <td className="border border-gray-400 px-2 py-1 text-right">{l.credit ? formatCurrency(l.credit) : '-'}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold">
                    <td className="border border-gray-400 px-2 py-1 text-right">Total</td>
                    <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency((data.lines || []).reduce((s, l) => s + (Number(l.debit) || 0), 0))}</td>
                    <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency((data.lines || []).reduce((s, l) => s + (Number(l.credit) || 0), 0))}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <table className="w-full border-collapse border border-gray-400 mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-400 px-2 py-1.5 text-left">Particulars</th>
                    <th className="border border-gray-400 px-2 py-1.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1">
                      {type === 'receipt' && `Amount received from ${party?.name || 'party'}`}
                      {type === 'payment' && `Amount paid to ${party?.name || 'party'}`}
                      {type === 'expense' && `${data.expenseHead || 'Expense'}`}
                      {type === 'contra' && `Transfer from ${data.fromAccount} to ${data.toAccount}`}
                      {data.againstInvoice && ` (against ${data.againstInvoice})`}
                      {data.againstBill && ` (against ${data.againstBill})`}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency(voucherAmount)}</td>
                  </tr>
                  <tr className="bg-gray-50 font-bold">
                    <td className="border border-gray-400 px-2 py-1 text-right">Total</td>
                    <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency(voucherAmount)}</td>
                  </tr>
                </tbody>
              </table>
            )}

            {(data.chequeNo || data.bankDetails || data.billRef) && (
              <div className="text-xs text-gray-600 mb-2">
                {data.bankDetails && <span className="mr-4">Bank: {data.bankDetails}</span>}
                {data.chequeNo && <span className="mr-4">Ref/Cheque No: {data.chequeNo}</span>}
                {data.billRef && <span>Bill Ref: {data.billRef}</span>}
              </div>
            )}
          </div>
        )}

        {/* ─────────── Production layout (inputs consumed / outputs produced) ─────────── */}
        {isProduction && (
          <div className="text-sm">
            <div className="flex justify-between mb-6">
              <div><span className="font-semibold">Voucher No:</span> {docNo}</div>
              {data.batchNo && <div><span className="font-semibold">Batch No:</span> {data.batchNo}</div>}
              <div><span className="font-semibold">Date:</span> {formatDate(data.date)}</div>
            </div>
            {[['inputs', 'Inputs — Raw Materials Consumed'], ['outputs', 'Outputs — Finished Goods Produced']].map(([key, heading]) => (
              <div key={key} className="mb-6">
                <h3 className="font-semibold mb-2">{heading}</h3>
                <table className="w-full border-collapse border border-gray-400">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 px-2 py-1.5 text-left">#</th>
                      <th className="border border-gray-400 px-2 py-1.5 text-left">Item</th>
                      <th className="border border-gray-400 px-2 py-1.5 text-right">Qty</th>
                      <th className="border border-gray-400 px-2 py-1.5 text-left">UOM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data[key] || []).map((l, i) => (
                      <tr key={i}>
                        <td className="border border-gray-400 px-2 py-1">{i + 1}</td>
                        <td className="border border-gray-400 px-2 py-1">{itemNameById(l.itemId)}</td>
                        <td className="border border-gray-400 px-2 py-1 text-right">{l.qty}</td>
                        <td className="border border-gray-400 px-2 py-1">{l.uom || itemUnitById(l.itemId)}</td>
                      </tr>
                    ))}
                    {(data[key] || []).length === 0 && (
                      <tr><td colSpan={4} className="border border-gray-400 px-2 py-2 text-center text-gray-400">None</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* ─────────── Stock Issue layout (items issued to consumption) ─────────── */}
        {isStockIssue && (
          <div className="text-sm">
            <div className="grid grid-cols-2 gap-8 mb-6">
              <div>
                <p><span className="font-semibold">Consumption Head:</span> {data.consumptionHead}</p>
                {data.issuedTo && <p><span className="font-semibold">Issued To:</span> {data.issuedTo}</p>}
              </div>
              <div className="text-right">
                <p><span className="font-semibold">Voucher No:</span> {docNo}</p>
                <p><span className="font-semibold">Date:</span> {formatDate(data.date)}</p>
              </div>
            </div>
            <table className="w-full border-collapse border border-gray-400 mb-4">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 px-2 py-1.5 text-left">#</th>
                  <th className="border border-gray-400 px-2 py-1.5 text-left">Item</th>
                  <th className="border border-gray-400 px-2 py-1.5 text-right">Qty</th>
                  <th className="border border-gray-400 px-2 py-1.5 text-left">UOM</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((l, i) => (
                  <tr key={i}>
                    <td className="border border-gray-400 px-2 py-1">{i + 1}</td>
                    <td className="border border-gray-400 px-2 py-1">{itemNameById(l.itemId)}</td>
                    <td className="border border-gray-400 px-2 py-1 text-right">{l.qty}</td>
                    <td className="border border-gray-400 px-2 py-1">{l.uom || itemUnitById(l.itemId)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ─────────── Item-based layout (invoices/bills/challan/returns/orders) ─────────── */}
        {!isVoucher && !isProduction && !isStockIssue && (
          <>
            <div className="grid grid-cols-2 gap-8 mb-6 text-sm">
              <div>
                <h3 className="font-semibold mb-1">{isPurchaseDoc ? 'Supplier' : 'Bill To'}:</h3>
                <p className="font-medium">{party?.name}</p>
                <p className="text-gray-600">{party?.address}</p>
                {party?.gstin && <p className="text-gray-600">GSTIN: {party.gstin}</p>}
                {party?.phone && <p className="text-gray-600">Phone: {party.phone}</p>}
                {!isPurchaseDoc && (data.shipToAddress || party?.shipToAddress) && (
                  <>
                    <h3 className="font-semibold mt-3 mb-1">Ship To:</h3>
                    <p className="text-gray-600 whitespace-pre-line">{data.shipToAddress || party.shipToAddress}</p>
                  </>
                )}
              </div>
              <div className="text-right">
                {type === 'invoice' ? (
                  <>
                    <p><span className="font-semibold">Invoice No:</span> {docNo}</p>
                    <p><span className="font-semibold">Invoice Date:</span> {formatDate(data.date)}</p>
                  </>
                ) : (
                  <>
                    <p><span className="font-semibold">No:</span> {docNo}</p>
                    <p><span className="font-semibold">Date:</span> {formatDate(data.date)}</p>
                    {data.billNo && <p><span className="font-semibold">Supplier Bill No:</span> {data.billNo}</p>}
                    {data.billDate && <p><span className="font-semibold">Bill Date:</span> {formatDate(data.billDate)}</p>}
                    {data.againstInvoice && <p><span className="font-semibold">Against:</span> {data.againstInvoice}</p>}
                    {data.dueDate && <p><span className="font-semibold">Due Date:</span> {formatDate(data.dueDate)}</p>}
                    {data.vehicleNo && <p><span className="font-semibold">Vehicle:</span> {data.vehicleNo}</p>}
                    {data.driverName && <p><span className="font-semibold">Driver:</span> {data.driverName}</p>}
                  </>
                )}
              </div>
            </div>

            <table className="items-table w-full border-collapse border border-gray-400 text-sm mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 px-2 py-1.5 text-left">#</th>
                  <th className="border border-gray-400 px-2 py-1.5 text-left">Item</th>
                  <th className="border border-gray-400 px-2 py-1.5 text-left">HSN/SAC</th>
                  <th className="border border-gray-400 px-2 py-1.5 text-right">Qty</th>
                  <th className="border border-gray-400 px-2 py-1.5 text-right">Rate</th>
                  {type !== 'challan' && (
                    <>
                      <th className="border border-gray-400 px-2 py-1.5 text-right">Disc%</th>
                      <th className="border border-gray-400 px-2 py-1.5 text-right">Taxable</th>
                      <th className="border border-gray-400 px-2 py-1.5 text-right">GST%</th>
                      <th className="border border-gray-400 px-2 py-1.5 text-right">Tax</th>
                      <th className="border border-gray-400 px-2 py-1.5 text-right">Net</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.items?.map((item, i) => {
                  const product = state.items.find(p => p.id === item.itemId);
                  return (
                    <tr key={i}>
                      <td className="border border-gray-400 px-2 py-1">{i + 1}</td>
                      <td className="border border-gray-400 px-2 py-1">{product?.name || item.itemName}</td>
                      <td className="border border-gray-400 px-2 py-1">{product?.hsnCode || item.hsnCode || '-'}</td>
                      <td className="border border-gray-400 px-2 py-1 text-right">{item.qty} {product?.unit}</td>
                      <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency(item.rate)}</td>
                      {type !== 'challan' && (
                        <>
                          <td className="border border-gray-400 px-2 py-1 text-right">{item.discountPercent || 0}%</td>
                          <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency(item.taxableAmount)}</td>
                          <td className="border border-gray-400 px-2 py-1 text-right">{item.taxPercent}%</td>
                          <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency(item.taxAmount)}</td>
                          <td className="border border-gray-400 px-2 py-1 text-right">{formatCurrency(item.netAmount)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {type !== 'challan' && (
              <div className="flex justify-end">
                <div className="w-72 text-sm">
                  <div className="flex justify-between py-1"><span>Subtotal:</span><span>{formatCurrency(data.subtotal)}</span></div>
                  {data.totalDiscount > 0 && (
                    <div className="flex justify-between py-1"><span>Discount:</span><span>-{formatCurrency(data.totalDiscount)}</span></div>
                  )}
                  <div className="flex justify-between py-1"><span>Taxable:</span><span>{formatCurrency(data.taxable)}</span></div>
                  {data.cgst > 0 && <div className="flex justify-between py-1"><span>CGST:</span><span>{formatCurrency(data.cgst)}</span></div>}
                  {data.sgst > 0 && <div className="flex justify-between py-1"><span>SGST:</span><span>{formatCurrency(data.sgst)}</span></div>}
                  {data.igst > 0 && <div className="flex justify-between py-1"><span>IGST:</span><span>{formatCurrency(data.igst)}</span></div>}
                  {data.roundOff !== 0 && (
                    <div className="flex justify-between py-1"><span>Round Off:</span><span>{formatCurrency(data.roundOff)}</span></div>
                  )}
                  <div className="flex justify-between py-2 border-t border-gray-400 font-bold text-base">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(data.grandTotal)}</span>
                  </div>
                  {data.tdsAmount > 0 && (
                    <>
                      <div className="flex justify-between py-1"><span>Less: TDS{data.tdsSection ? ` (${data.tdsSection})` : ''} @ {data.tdsRate}%:</span><span>-{formatCurrency(data.tdsAmount)}</span></div>
                      <div className="flex justify-between py-1 border-t border-gray-300 font-semibold">
                        <span>{isPurchaseDoc ? 'Net Payable:' : 'Net Receivable:'}</span>
                        <span>{formatCurrency(data.netAmount != null ? data.netAmount : (data.grandTotal - data.tdsAmount))}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {type !== 'challan' && co.bankName && co.bankAccountNo && (
              <div className="mt-6 text-xs border border-gray-300 rounded p-3">
                <p className="font-semibold mb-1">Bank Details for Payment:</p>
                <div className="grid grid-cols-2 gap-1">
                  <span>Bank: {co.bankName}{co.bankBranch ? `, ${co.bankBranch}` : ''}</span>
                  <span>A/c No: {co.bankAccountNo}</span>
                  <span>IFSC: {co.bankIfsc}</span>
                  <span>A/c Type: {co.bankAccountType}</span>
                </div>
              </div>
            )}
          </>
        )}

        {data.narration && (
          <div className="mt-4 text-sm">
            <span className="font-semibold">Narration: </span>{data.narration}
          </div>
        )}

        {co.termsAndConditions && !isVoucher && (
          <div className="mt-4 text-[10px] text-gray-500">
            <p className="font-semibold text-gray-600 mb-0.5">Terms & Conditions:</p>
            <pre className="whitespace-pre-wrap font-sans">{co.termsAndConditions}</pre>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 flex justify-between text-sm">
          <div>
            {co.invoiceNotes && !isVoucher && <p className="text-xs text-gray-500 italic mb-8">{co.invoiceNotes}</p>}
            <p className="border-t border-gray-400 pt-1">{isVoucher ? "Receiver's Signature" : "Receiver's Signature"}</p>
          </div>
          <div className="text-right">
            <p className="font-semibold mb-2">For {co.name}</p>
            {co.signature
              ? <img src={co.signature} alt="Signature" className="inline-block h-14 object-contain mb-1" />
              : <div className="h-14" />}
            <p className="border-t border-gray-400 pt-1">{co.signatory || 'Authorized Signatory'}</p>
            {co.designation && <p className="text-xs text-gray-500">{co.designation}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
